import { handleSystemErrorReport } from '@mb/lib/utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../../config';
import { getEmail } from '../../helpers';
import { RootState } from '../../redux/store';
import {
  DefaultSuggestionResponse,
  DeleteItemResponse,
  HistoryResponse,
  InitAutocompleteResult,
  SuggestionResponse,
  SuggestionResponseWithRequestId,
} from '../../types';

const DEFAULT_ERROR_MESSAGE = 'Failed to fetch autocomplete data.';
const DEFAULT_ERROR_MESSAGE_SUGGESTIONS = 'Failed to fetch suggestions.';

export const initAutocomplete = createAsyncThunk<
  InitAutocompleteResult,
  string,
  { state: RootState }
>('autocomplete/init', async (payload, thunkApi) => {
  try {
    const email = getEmail();

    if (payload.length > 2) {
      const response = await axios.get<SuggestionResponse>(
        config.endpoints.getSuggestions(payload, ''),
      );
      if (response.status !== 200) {
        return thunkApi.rejectWithValue({
          message: DEFAULT_ERROR_MESSAGE_SUGGESTIONS,
        });
      }
      return { suggestions: response.data };
    }

    let history: HistoryResponse | undefined = undefined;

    if (email !== null && email.length > 0) {
      const response = await axios.get<HistoryResponse>(
        config.endpoints.getHistoryByEmail(email),
      );

      if (response.status !== 200) {
        return thunkApi.rejectWithValue({
          message: DEFAULT_ERROR_MESSAGE,
        });
      }
      history = response.data;
    }
    let defaultSuggestions: DefaultSuggestionResponse[] | undefined = undefined;

    const response = await axios.get<DefaultSuggestionResponse[]>(
      config.endpoints.getDefaultSuggestions,
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    defaultSuggestions = response.data;

    const currentItem =
      history?.entries?.[0]?.query ||
      defaultSuggestions?.[0]?.values?.[0]?.query;
    let itemsList = null;
    let itemsQuery = undefined;

    if (currentItem != null) {
      const response = await axios.get<SuggestionResponse>(
        config.endpoints.getSuggestions(currentItem, '&action=hover'),
      );
      itemsList = response.data?.items;
      itemsQuery = response?.data?.query;
    }
    return { defaultSuggestions, history, items: itemsList || [], itemsQuery };
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to fetch autocomplete data. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});

export const fetchDefaultSuggestions = createAsyncThunk<
  DefaultSuggestionResponse[],
  void,
  { state: RootState }
>('defaultSuggestions/get', async (_, thunkApi) => {
  try {
    const response = await axios.get<DefaultSuggestionResponse[]>(
      config.endpoints.getDefaultSuggestions,
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    return response.data;
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    return thunkApi.rejectWithValue({ message });
  }
});

export const fetchHistoryByEmail = createAsyncThunk<
  HistoryResponse,
  string,
  { state: RootState }
>('history/get', async (payload, thunkApi) => {
  try {
    const response = await axios.get<HistoryResponse>(
      config.endpoints.getHistoryByEmail(payload),
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    return response.data;
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to fetch history data. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});

export const fetchSuggestionsByQuery = createAsyncThunk<
  SuggestionResponseWithRequestId,
  string,
  { state: RootState }
>('suggestions/get', async (payload, thunkApi) => {
  try {
    const response = await axios.get<SuggestionResponse>(
      config.endpoints.getSuggestions(payload, ''),
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    return {
      data: response.data,
      xRequestId: response.headers?.['x-request-id'],
    };
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to fetch suggestions data. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});

export const clearHistoryByEmail = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>('history/delete', async (_, thunkApi) => {
  try {
    const email = getEmail();

    if (email === null) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }

    const response = await axios.delete<unknown>(
      config.endpoints.clearHistory(email),
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    return response.data;
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to clear history data. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});

export const deleteHistoryItem = createAsyncThunk<
  DeleteItemResponse,
  string,
  { state: RootState }
>('historyItem/delete', async (payload, thunkApi) => {
  try {
    const email = getEmail();

    if (email === null) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }

    const response = await axios.delete(
      config.endpoints.deleteHistoryItem(payload, email),
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
    return { ...response.data, value: payload };
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to delete history item. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});

export const fetchSuggestionsItems = createAsyncThunk<
  SuggestionResponseWithRequestId,
  string,
  { state: RootState }
>('suggestionItems/get', async (payload, thunkApi) => {
  try {
    const response = await axios.get<SuggestionResponse>(
      config.endpoints.getSuggestions(payload, '&action=hover'),
    );

    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message: DEFAULT_ERROR_MESSAGE,
      });
    }

    return {
      data: response.data,
      xRequestId: response.headers?.['x-request-id'],
    };
  } catch (e: any) {
    const message = e.response?.data?.message ?? DEFAULT_ERROR_MESSAGE;
    handleSystemErrorReport(
      `Failed to fetch suggestions items data. ${message}`,
      'autocomplete',
    );
    return thunkApi.rejectWithValue({ message });
  }
});
